import React, { useEffect, useState } from "react";
import { Form } from "react-bootstrap";
import { useParams } from 'react-router-dom';
import { UncachedImage } from "react-uncached-image";
import styles from "../styles/UserRecipeBrowser.module.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
  faUtensils, 
  faClock, 
  faTag, 
  faDollarSign,
  faFire,
  faImage,
  faSearch,
  faChevronUp,
  faChevronDown,
  faList
} from "@fortawesome/free-solid-svg-icons";

function UserRecipeBrowser() {
  const { url } = useParams();
  const [isLoggedIn, setIsLoggedIn] = useState(true);
  const [recipeUserDisplayName, setRecipeUserDisplayName] = useState("");
  const [recipes, setRecipes] = useState([]);
  const [isTable, setIsTable] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Table View Variables
  const [searchQuery, setSearchQuery] = useState("");
  const [sortedColumn, setSortedColumn] = useState("name");
  const [sortDirection, setSortDirection] = useState("asc");

  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_URL}/api/isLoggedIn`, {
      credentials: "include",
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.isLoggedIn) {
          setIsLoggedIn(true);
        } else {
          throw new Error("User is not logged in");
        }
      })
      .catch((error) => {
        console.error("Error checking if user is logged in:", error);
        setIsLoggedIn(false);
      });
  }, []);

  const tokenizer = (query) =>
    query
      .toLowerCase()
      .split(/[\s,]+/)
      .filter((token) => token.trim().length > 0);
    
  const filteredRecipes = recipes.filter(
    (recipe) =>
      tokenizer(searchQuery).every((token) =>
        [
          recipe.name.toLowerCase(),
          recipe.tags?.toLowerCase() || "",
          recipe.course?.toLowerCase() || "",
        ].some((field) => field.includes(token))
      )
  );

  const handleSort = (columnName) => {
    if (sortedColumn === columnName) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortedColumn(columnName);
      setSortDirection("asc");
    }
  };

  const sortedRecipes = [...filteredRecipes].sort((a, b) => {
    let aValue, bValue;
    if (sortedColumn === "calories") {
      aValue = parseFloat(a.total_calories / a.servings);
      bValue = parseFloat(b.total_calories / b.servings);
    } else if (sortedColumn === "cost") {
      aValue = parseFloat(a.total_price / a.servings);
      bValue = parseFloat(b.total_price / b.servings);
    } else {
      aValue = a[sortedColumn];
      bValue = b[sortedColumn];
    }
    
    if (sortDirection === "asc") {
      return aValue > bValue ? 1 : -1;
    } else {
      return aValue < bValue ? 1 : -1;
    }
  });

  const fetchUserSetting = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/getUser`,
        {
          method: "GET",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
      const { user } = await response.json();

      if (user) {
        setIsTable(user.view_as_table === 1);
      }
    } catch (error) {
      console.error("Error fetching user settings:", error);
    }
  };

  const handleToggleLayout = () => {
    setIsTable(!isTable);
    
    if (isLoggedIn) {
      fetch(`${process.env.REACT_APP_API_URL}/api/updateUser/table_view`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          view_as_table: isTable ? 0 : 1,
        }),
      }).then((response) => {
        if (!response.ok) {
          console.error("Error updating view preference");
        }
      });
    }
  };

  // Fetch Recipes List
  useEffect(() => {
    setIsLoading(true);
    fetch(`${process.env.REACT_APP_API_URL}/api/public_user_recipes/${url}`, {
      credentials: "include",
    })
      .then((res) => {
        if (res.ok) {
          return res.json();
        } else {
          throw new Error("Error retrieving recipes from server");
        }
      })
      .then((data) => {
        const publicRecipes = data.filter(recipe => recipe.hidden === 0 && recipe.public === 1);
        setRecipes(publicRecipes);
        setIsLoading(false);
      })
      .catch((err) => {
        console.error(err);
        setIsLoading(false);
      });
  }, [url]);

  // Fetch User Display Name
  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_URL}/api/public_user_recipes_display_name/${url}`, {
      credentials: "include",
    })
      .then((res) => {
        if (res.ok) {
          return res.json();
        } else {
          throw new Error("Error retrieving user display name");
        }
      })
      .then((data) => {
        setRecipeUserDisplayName(data.display_name);
      })
      .catch((err) => {
        console.error(err);
      });
  }, [url]);
 
  useEffect(() => {
    if (isLoggedIn) {
      fetchUserSetting();
    }
  }, [isLoggedIn]);

  const renderSortIcon = (column) => {
    if (sortedColumn !== column) {
      return <FontAwesomeIcon icon={faChevronDown} style={{ opacity: 0.3 }} />;
    }
    return sortDirection === "asc" ? 
      <FontAwesomeIcon icon={faChevronUp} /> : 
      <FontAwesomeIcon icon={faChevronDown} />;
  };

  if (isLoading) {
    return (
      <div className={styles.browserContainer}>
        <div className={styles.loading}>
          <div className={styles.loadingSpinner}></div>
          <p>Loading recipes...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.browserContainer}>
      {/* Header Section */}
      <div className={styles.browserHeader}>
        <div className={styles.headerContent}>
          <div>
            <h2 style={{ margin: 0, fontSize: '1.125rem', fontWeight: 600, color: '#212529' }}>
              Recipe Browser
            </h2>
          </div>
          <div className={styles.headerControls}>
            {isLoggedIn && (
              <div className={styles.viewToggle}>
                <Form.Switch
                  type="switch"
                  id="view-switch"
                  label="Table View"
                  checked={isTable}
                  onChange={handleToggleLayout}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* User Title */}
      {recipeUserDisplayName && (
        <div className={styles.userTitle}>
          <h1>{recipeUserDisplayName}'s Recipes</h1>
        </div>
      )}

      {/* Search Section */}
      <div className={styles.searchSection}>
        <div className="input-group">
          <span className="input-group-text">
            <FontAwesomeIcon icon={faSearch} />
          </span>
          <input
            type="text"
            className={`form-control ${styles.searchInput}`}
            placeholder="Search recipes by name, course, or tags..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      </div>

      {/* Sort Controls (Grid View) */}
      {!isTable && (
        <div className={styles.sortControls}>
          <span className={styles.sortLabel}>Sort by:</span>
          <button
            className={`${styles.sortButton} ${sortedColumn === "name" ? styles.active : ""}`}
            onClick={() => handleSort("name")}
          >
            Recipe {renderSortIcon("name")}
          </button>
          <button
            className={`${styles.sortButton} ${sortedColumn === "course" ? styles.active : ""}`}
            onClick={() => handleSort("course")}
          >
            Course {renderSortIcon("course")}
          </button>
          <button
            className={`${styles.sortButton} ${sortedColumn === "servings" ? styles.active : ""}`}
            onClick={() => handleSort("servings")}
          >
            Servings {renderSortIcon("servings")}
          </button>
          <button
            className={`${styles.sortButton} ${sortedColumn === "calories" ? styles.active : ""}`}
            onClick={() => handleSort("calories")}
          >
            Calories {renderSortIcon("calories")}
          </button>
          <button
            className={`${styles.sortButton} ${sortedColumn === "cost" ? styles.active : ""}`}
            onClick={() => handleSort("cost")}
          >
            Cost {renderSortIcon("cost")}
          </button>
          <button
            className={`${styles.sortButton} ${sortedColumn === "tags" ? styles.active : ""}`}
            onClick={() => handleSort("tags")}
          >
            Tags {renderSortIcon("tags")}
          </button>
        </div>
      )}

      {/* Empty State */}
      {sortedRecipes.length === 0 && (
        <div className={styles.emptyState}>
          <FontAwesomeIcon icon={faUtensils} />
          <h3>No recipes found</h3>
          <p>Try adjusting your search terms</p>
        </div>
      )}

      {/* Grid View */}
      {!isTable && sortedRecipes.length > 0 && (
        <div className={styles.recipeGrid}>
          {sortedRecipes.map((recipe) => (
            <div
              key={recipe.recipe_id}
              className={styles.recipeCard}
              onClick={() => {
                window.location.href = `${process.env.REACT_APP_CLIENT_URL}/view-recipe/${recipe.recipe_id}`;
              }}
            >
              {recipe.image_url ? (
                <UncachedImage
                  className={styles.recipeImage}
                  src={recipe.image_url}
                  alt={recipe.name}
                />
              ) : (
                <div className={styles.noImage}>
                  <div>
                    <FontAwesomeIcon icon={faImage} />
                    <p>No image</p>
                  </div>
                </div>
              )}
              
              <div className={styles.cardBody}>
                <h3 className={styles.cardTitle}>
                  <a
                    href={`${process.env.REACT_APP_CLIENT_URL}/view-recipe/${recipe.recipe_id}`}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {recipe.name}
                  </a>
                </h3>
                
                <div className={styles.cardMeta}>
                  {recipe.course && (
                    <div className={styles.metaItem}>
                      <FontAwesomeIcon icon={faUtensils} />
                      <span>{recipe.course}</span>
                    </div>
                  )}
                  <div className={styles.metaItem}>
                    <FontAwesomeIcon icon={faClock} />
                    <span>{recipe.servings} servings</span>
                  </div>
                </div>
                
                <div className={styles.statsRow}>
                  <div className={styles.statItem}>
                    <span className={styles.statLabel}>Calories</span>
                    <span className={styles.statValue}>
                      {parseFloat(recipe.total_calories / recipe.servings).toFixed(0)}
                    </span>
                  </div>
                  <div className={styles.statItem}>
                    <span className={styles.statLabel}>Per Serving</span>
                    <span className={`${styles.statValue} ${styles.price}`}>
                      ${parseFloat(recipe.total_price / recipe.servings).toFixed(2)}
                    </span>
                  </div>
                </div>
                
                {recipe.tags && (
                  <div className={styles.tags}>
                    <FontAwesomeIcon icon={faTag} style={{ marginRight: '6px' }} />
                    {recipe.tags}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Table View */}
      {isTable && sortedRecipes.length > 0 && (
        <div className={styles.tableContainer}>
          <div className="table-responsive">
            <table className={`table ${styles.recipeTable}`}>
              <thead>
                <tr>
                  <th 
                    className={sortedColumn === "name" ? styles.active : ""}
                    onClick={() => handleSort("name")}
                  >
                    Recipe {renderSortIcon("name")}
                  </th>
                  <th 
                    className={`text-center ${sortedColumn === "course" ? styles.active : ""}`}
                    onClick={() => handleSort("course")}
                  >
                    Course {renderSortIcon("course")}
                  </th>
                  <th 
                    className={`text-center ${sortedColumn === "servings" ? styles.active : ""}`}
                    onClick={() => handleSort("servings")}
                  >
                    Servings {renderSortIcon("servings")}
                  </th>
                  <th 
                    className={`text-center ${sortedColumn === "calories" ? styles.active : ""}`}
                    onClick={() => handleSort("calories")}
                  >
                    Calories/Serving {renderSortIcon("calories")}
                  </th>
                  <th 
                    className={`text-center ${sortedColumn === "cost" ? styles.active : ""}`}
                    onClick={() => handleSort("cost")}
                  >
                    Cost/Serving {renderSortIcon("cost")}
                  </th>
                  <th 
                    className={`text-center ${sortedColumn === "tags" ? styles.active : ""}`}
                    onClick={() => handleSort("tags")}
                  >
                    Tags {renderSortIcon("tags")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {sortedRecipes.map((recipe) => (
                  <tr 
                    key={recipe.recipe_id}
                    style={{ cursor: 'pointer' }}
                    onClick={() => {
                      window.location.href = `${process.env.REACT_APP_CLIENT_URL}/view-recipe/${recipe.recipe_id}`;
                    }}
                  >
                    <td className={styles.recipeName}>
                      <a
                        href={`${process.env.REACT_APP_CLIENT_URL}/view-recipe/${recipe.recipe_id}`}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {recipe.name}
                      </a>
                    </td>
                    <td className="text-center">{recipe.course || '-'}</td>
                    <td className="text-center">{recipe.servings}</td>
                    <td className="text-center">
                      {parseFloat(recipe.total_calories / recipe.servings).toFixed(0)}
                    </td>
                    <td className="text-center">
                      ${parseFloat(recipe.total_price / recipe.servings).toFixed(2)}
                    </td>
                    <td className="text-center">
                      {recipe.tags ? 
                        (recipe.tags.length > 50 ? 
                          recipe.tags.slice(0, 50) + "..." : 
                          recipe.tags) 
                        : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

export default UserRecipeBrowser;